import { useRef, useCallback } from 'react'
import { playAudio, stopAudio } from '@/utils/audioPlayback'

const ringtonesSound = 'https://assets.myjobglasses.com/immersion/phone-calls/ringtones.mp3'

/**
 * Hook to play ringtones audio before connecting to a phone meeting
 * @returns Function to play ringtones and wait for it to finish
 */
export function usePhoneRingtones() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const lastSpeakerDeviceIdRef = useRef<string | undefined>(undefined)

  const playRingtones = useCallback(async (speakerDeviceId?: string): Promise<void> => {
    return new Promise((resolve) => {
      lastSpeakerDeviceIdRef.current = speakerDeviceId

      playAudio({
        audioFile: ringtonesSound,
        speakerDeviceId,
        volume: 1.0
      })
        .then((audio) => {
          audioRef.current = audio

          const handleEnded = () => {
            audio.removeEventListener('ended', handleEnded)
            audio.removeEventListener('error', handleError)
            resolve()
          }

          const handleError = (error: Event) => {
            audio.removeEventListener('ended', handleEnded)
            audio.removeEventListener('error', handleError)
            console.error('[PhoneRingtones] Error playing ringtones:', error)
            // Resolve anyway to not block the join process
            resolve()
          }

          audio.addEventListener('ended', handleEnded)
          audio.addEventListener('error', handleError)
        })
        .catch((error) => {
          // Autoplay might be blocked, resolve anyway
          console.warn('[PhoneRingtones] Could not play ringtones:', error)
          resolve()
        })
    })
  }, [])

  const stopRingtones = useCallback((speakerDeviceId?: string) => {
    stopAudio({
      audioFile: ringtonesSound,
      speakerDeviceId: speakerDeviceId ?? lastSpeakerDeviceIdRef.current
    })
  }, [])

  return { playRingtones, stopRingtones }
}
